/**
 * Echo Hunt -- story progression (P11, §8.3 + §8.4).
 *
 * A story is a run of rounds following ONE pool character through one or more worlds. This module owns
 * the parts of that which are pure and therefore testable without a DB:
 *
 *  1. **The suspicion meter** (§8.4) -- the float, how it moves, and whether it is ACTED ON.
 *  2. **The door** (§8.3) -- what "go through" means, and which world the next round is set in.
 *  3. **The world-visit history** (§14) -- an ordered record of where the character has been.
 *
 * The DB-facing half is `Story_Live.ts`; the UI reads its view models from there.
 *
 * ## The suspicion meter is recorded unconditionally, but only ACTED ON when enabled (§8.4)
 *
 * This is the one deliberate deviation from the phase text, and it is the user's call: "the user should
 * be able to disable suspicion-application, such that it doesn't affect the story generation, nor cause
 * the story to climax/end".
 *
 * So the two things are separated rather than conflated:
 *
 *  - `suspicion` keeps accumulating on the story whatever the settings say. It is an honest record of how
 *    often the user found their own echoes, and it is the input to every §13.2 correlation the stats
 *    phase will want. Discarding it would mean a player who turns the feature back on starts from
 *    nothing, and the rounds played in between are unrecoverable.
 *  - `EchoSuspicionSettings.applied` gates whether crossing the threshold DOES anything: the lucid
 *    interlude and the end of the story. With it off, the meter is a readout. Nothing about the weave,
 *    the judge loop or the round's own events changes either way -- suspicion is computed from a
 *    finished round's score, and no prompt ever sees it.
 *
 * A consequence worth keeping: because the meter is not fed into any prompt, it cannot leak state into
 * the weaver. §8.4 notes the meter "depends only on past scores, so it leaks nothing about upcoming
 * labels" -- that property survives this design trivially. (The ONE prompt that ever sees the number is
 * the interlude's, and it runs after the story has already ended, on no bullets at all.)
 *
 * ## Why the story's world is DERIVED rather than stored (§11.5 W1)
 *
 * The world a story is in legitimately changes as it progresses, so "the story's world" is a cursor, not
 * a property. Storing it as `story.worldId` gave a SECOND answer to a question `worldHistory` already
 * answered, and every drift bug in §11.5 was those two answers disagreeing (a substituted world never
 * written back, a manual move leaving a round behind, a mirrored `doorPending` flag going stale).
 *
 * So the rule is now: **a story's location changes only by ONE append to `worldHistory`.** There is no
 * other write path, and `CurrentWorldId` is the only read. `startingWorldId` is deliberately not a field
 * either -- it is `history[0]`, which `CreateStory_Live` already writes.
 *
 * ## Why the door has three actors (§11.5 W2)
 *
 * The weaver PROPOSES a way out (and fixes where it leads), the PLAYER ratifies whether that possibility
 * should exist in the story at all, and the CHARACTER decides whether to walk through it. Confusing the
 * last two is what made the shipped code say both "the user decides" and "the character decides". The
 * player's half is authorial and happens outside the fiction (at the reveal, where no in-story knowledge
 * is assumed); the character's half is in-fiction and is what actually moves the story.
 *
 * PURE module: no React, no `store`, no DB imports (see README.md).
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §8.3, §8.4, §11.5, §14, §16.2 P11.
 */
import {DoorVerdict, EchoDoorOffer, EchoSuspicionSettings, EchoWorld, WorldChangeCause, WorldVisit} from "./Types.js";
import {PickRandomWorld} from "./Worlds.js";

// suspicion settings (§8.4)
// ==========

/** §8.4 gives no numbers, so these are the shipped defaults and the ones the settings panel starts
 * from. `threshold` is reached after about four unbroken finds, which is a story's worth of rounds --
 * fast enough to be reachable in a session, slow enough that one lucky round does not end a run. */
export const DEFAULT_SUSPICION_THRESHOLD = .6;
export const DEFAULT_SUSPICION_GAIN_PER_HIT = .18;
export const DEFAULT_SUSPICION_LOSS_PER_MISS = .05;

/** The floor a stored threshold is clamped to. 0 would mean "every round ends the story", which is never
 * what a user meant to configure -- and unlike a warning, a clamp cannot be missed. */
export const MIN_SUSPICION_THRESHOLD = .05;

export function DefaultSuspicionSettings(): EchoSuspicionSettings {
	return {
		// ON by default: §8.4 is part of the design, and a feature that ships off is one nobody discovers.
		applied: true,
		threshold: DEFAULT_SUSPICION_THRESHOLD,
		gainPerHit: DEFAULT_SUSPICION_GAIN_PER_HIT,
		lossPerMiss: DEFAULT_SUSPICION_LOSS_PER_MISS,
	};
}

/** Clamps a settings object into a usable range. Missing/absurd values fall back to the defaults rather
 * than making the meter unusable: a `NaN` gain would pin suspicion at `NaN` forever, and the story would
 * silently never end. */
export function NormalizeSuspicionSettings(settings?: Partial<EchoSuspicionSettings>|n): EchoSuspicionSettings {
	const defaults = DefaultSuspicionSettings();
	const finiteOr = (value: number|n|undefined, fallback: number): number=>(value != null && Number.isFinite(value) ? value : fallback);

	return {
		// `applied` defaults to ON, so an absent field on a pre-P11 profile behaves like a fresh one
		applied: settings?.applied ?? defaults.applied,
		threshold: Math.max(MIN_SUSPICION_THRESHOLD, Math.min(1, finiteOr(settings?.threshold, defaults.threshold))),
		gainPerHit: Math.max(0, Math.min(1, finiteOr(settings?.gainPerHit, defaults.gainPerHit))),
		lossPerMiss: Math.max(0, Math.min(1, finiteOr(settings?.lossPerMiss, defaults.lossPerMiss))),
	};
}

/** True when the settings keep a stored threshold that will be silently clamped, so the panel can say so
 * instead of showing a number the meter is not honouring. */
export function ThresholdIsClamped(settings?: Partial<EchoSuspicionSettings>|n): boolean {
	const raw = settings?.threshold;
	if (raw == null || !Number.isFinite(raw)) return false;
	return raw < MIN_SUSPICION_THRESHOLD;
}

// the meter (§8.4)
// ==========

export function ClampSuspicion(value: number|n): number {
	if (value == null || !Number.isFinite(value)) return 0;
	return Math.max(0, Math.min(1, value));
}

/** The fields the meter reads off a story. Structural, so `EchoStory` satisfies it. */
export type SuspicionStoryFields = {
	/** the value the STORY has accumulated; only rounds move it */
	suspicionAccumulated?: number|n;
	/** a signed offset the user applied by hand; 0/absent = no adjustment */
	suspicionUserAdjustment?: number|n;
};

/** The pre-P11 field name, read as a fallback so rows written before the split still show their value.
 * Deliberately not written any more -- see `SuspicionStoryFields`. */
type LegacySuspicionField = {suspicion?: number|n};

/** The story-derived value, ignoring any hand adjustment. */
export function GetAccumulatedSuspicion(story: (SuspicionStoryFields & LegacySuspicionField)|n): number {
	const stored = story?.suspicionAccumulated ?? story?.suspicion ?? 0;
	return ClampSuspicion(stored);
}

/** The user's hand-set offset. Signed, and NOT clamped on its own -- a -40% offset is meaningful even
 * though it is not a valid meter reading; only the sum is clamped. */
export function GetSuspicionAdjustment(story: SuspicionStoryFields|n): number {
	const value = story?.suspicionUserAdjustment;
	if (value == null || !Number.isFinite(value)) return 0;
	// snapped to 1%, so the slider and the stored value cannot disagree by a rounding artifact
	return Math.round(value * 100) / 100;
}

/** THE meter: what the threshold is compared against, and what every panel displays. A function of two stored
 * fields (accumulated + offset) rather than one overwritten value, so the story's record is never destroyed.
 * A LEGACY `suspicion` field is read when the new one is absent, so pre-split rows still show their meter. */
export function GetEffectiveSuspicion(story: (SuspicionStoryFields & LegacySuspicionField)|n): number {
	return ClampSuspicion(GetAccumulatedSuspicion(story) + GetSuspicionAdjustment(story));
}

/** True when a hand adjustment is in effect, so the UI can offer to reset it. */
export function HasSuspicionAdjustment(story: SuspicionStoryFields|n): boolean {
	return GetSuspicionAdjustment(story) != 0;
}

/** The score fields the meter reads. Structural, so `HuntScore` satisfies it without this module having
 * to import the scorer. */
export type SuspicionScoreInput = {
	hits: number;
	misses: number;
};

export type SuspicionUpdate = {
	before: number;
	after: number;
	/** the signed change actually applied (before clamping) */
	delta: number;
	/** true when the meter is past its threshold AND acting on it is enabled -- i.e. the story ends */
	crossed: boolean;
};

/** Moves the meter by one finished round, returning the new ACCUMULATED value. `crossed` is judged on the
 * EFFECTIVE meter (accumulated + adjustment), so a hand offset really does shift when the character notices. */
export function ComputeSuspicionChange(accumulated: number|n, score: SuspicionScoreInput|n, settings?: Partial<EchoSuspicionSettings>|n, adjustment?: number|n): SuspicionUpdate {
	const normalized = NormalizeSuspicionSettings(settings);
	const start = ClampSuspicion(accumulated);
	const adjustmentValue = adjustment ?? 0;

	const hits = Math.max(0, score?.hits ?? 0);
	const misses = Math.max(0, score?.misses ?? 0);
	const delta = hits * normalized.gainPerHit - misses * normalized.lossPerMiss;
	const after = ClampSuspicion(start + delta);

	return {
		before: ClampSuspicion(start + adjustmentValue), // the meter as SHOWN before this round
		after: after, // the accumulated value after it
		delta: after - start, // the APPLIED delta, so a clamped round reports what really happened
		crossed: normalized.applied && ClampSuspicion(after + adjustmentValue) >= normalized.threshold,
	};
}

/** True when the meter has reached the threshold under these settings, and applying it is enabled. */
export function IsSuspicionThresholdReached(story: SuspicionStoryFields|n, settings?: Partial<EchoSuspicionSettings>|n): boolean {
	const normalized = NormalizeSuspicionSettings(settings);
	if (!normalized.applied) return false;
	return GetEffectiveSuspicion(story) >= normalized.threshold;
}

/** Wording for the meter (§8.4), so the panel, the reveal and the settings preview cannot disagree. */
export function DescribeSuspicion(suspicion: number|n, settings?: Partial<EchoSuspicionSettings>|n): string {
	const normalized = NormalizeSuspicionSettings(settings);
	const value = ClampSuspicion(suspicion);

	if (!normalized.applied) {
		return `${Math.round(value * 100)}% -- recorded only: applying suspicion is OFF, so this never ends the story`;
	}
	if (value >= normalized.threshold) {
		return `${Math.round(value * 100)}% -- over the ${Math.round(normalized.threshold * 100)}% threshold: the character notices`;
	}
	return `${Math.round(value * 100)}% of the way to noticing (threshold ${Math.round(normalized.threshold * 100)}%)`;
}

// the story's location (§11.5 W1) -- DERIVED, never stored
// ==========

/** The minimal story shape the location rule needs. Structural, so the DB class satisfies it. */
export type LocatableStory = {
	worldHistory?: WorldVisit[]|n;
};

/** The minimal round shape it falls back to. Structural, and deliberately narrow. */
export type LocatableRound = {
	worldId?: string|n;
};

/** WHERE THE STORY IS. The only read of a story's location anywhere in the feature.
 *
 * Last entry of `worldHistory` wins. When the history is empty -- every pre-P11 story, and any story
 * created before the field existed -- it falls back to the NEWEST round's `worldId`, because rounds have
 * always carried one. That fallback is what makes removing `story.worldId` a no-migration change instead
 * of a breakage: no existing story becomes unplayable.
 *
 * Returns null only when there is neither a visit nor a round, i.e. a story that has never placed the
 * character anywhere. */
export function CurrentWorldId(story: LocatableStory|n, rounds?: LocatableRound[]|n): string|null {
	const history = story?.worldHistory ?? [];
	const last = history[history.length - 1];
	if (last != null && last.worldId.length > 0) return last.worldId;

	// pre-11.5 fallback: the newest round knows where it was written
	const ordered = [...(rounds ?? [])];
	const newest = ordered[ordered.length - 1];
	return newest?.worldId ?? null;
}

/** The name the history recorded for a world, for a UI that must name a world the pool may no longer
 * contain. Null when the world was never entered, or was entered before names were recorded. */
export function WorldNameFromHistory(story: LocatableStory|n, worldId: string|null|n): string|null {
	if (worldId == null) return null;
	const history = story?.worldHistory ?? [];
	// search backwards: the most recent visit has the freshest name
	for (let i = history.length - 1; i >= 0; i--) {
		const visit = history[i];
		if (visit.worldId == worldId && visit.worldName != null && visit.worldName.length > 0) return visit.worldName;
	}
	return null;
}

/** A move the story has made but has not yet told the weaver about (§11.5 W4).
 *
 * "Pending" means: the newest visit started at or after the number of rounds played, so no round has been
 * written in that world yet. Derived rather than flagged, so it cannot go stale -- after one round in the
 * new world the condition is false by construction and the block disappears on its own. */
export type PendingWorldTransition = {
	fromWorldId: string|null;
	fromWorldName: string|null;
	toWorldId: string;
	toWorldName: string|null;
	cause: WorldChangeCause|null;
	doorDescription: string|null;
};

/** The transition the NEXT round must narrate, or null when the story is not mid-move.
 *
 * `roundsPlayed` is the count of rounds in the story: a visit stamped with that index or higher belongs to
 * a future round, i.e. the move has happened in the story but not yet in any installment. */
export function PendingWorldTransition(story: LocatableStory|n, roundsPlayed: number): PendingWorldTransition|null {
	const history = story?.worldHistory ?? [];
	if (history.length < 2) return null;

	const last = history[history.length - 1];
	if (last == null) return null;
	// already told: a round exists in this world
	if (last.startedAtRoundIndex < Math.max(0, Math.round(roundsPlayed))) return null;

	const previous = history[history.length - 2];
	return {
		fromWorldId: last.fromWorldId ?? previous?.worldId ?? null,
		fromWorldName: last.fromWorldName ?? previous?.worldName ?? null,
		toWorldId: last.worldId,
		toWorldName: last.worldName ?? null,
		cause: last.cause ?? null,
		doorDescription: last.doorDescription ?? null,
	};
}

// the door (§8.3, §11.5 W2) -- three actors, three questions
// ==========

/** Why a door cannot be answered, if it cannot. `staleRound` = the round is no longer the story's newest;
 * `alreadyStaged` = a door is already waiting for the CHARACTER, so staging another would queue two. */
export type DoorBlockReason = "ended"|"noOffer"|"alreadyAnswered"|"staleRound"|"alreadyStaged";

export type DoorAnswerability =
	| {ok: true}
	| {ok: false, reason: DoorBlockReason};

/** The minimal round shape the gate reads. Structural, so `EchoRound` satisfies it. */
export type DoorGateRound = {
	id: string;
	createdAt?: number|n;
	doorOffer?: EchoDoorOffer|n;
	doorVerdict?: DoorVerdict|n;
	doorDecision?: string|n;
};

/** Whether the PLAYER may record a verdict on this round's door (§11.5 W5).
 *
 * One live door per story, always on the story's NEWEST round. Everything that used to go wrong is a case
 * of this gate not existing: answering twice, answering another story's door (the recent-reveals list is
 * account-wide), answering a door three rounds stale, and queueing a second door behind a first. */
export function CanAnswerDoor(story: {ended?: boolean|n}|n, round: DoorGateRound|n, rounds: DoorGateRound[]|n): DoorAnswerability {
	if (story?.ended == true) return {ok: false, reason: "ended"};
	if (round == null) return {ok: false, reason: "noOffer"};
	if (round.doorOffer == null) return {ok: false, reason: "noOffer"};
	if (round.doorVerdict != null) return {ok: false, reason: "alreadyAnswered"};

	// "newest" is decided by identity against the story's own round list, not by index arithmetic
	const ordered = [...(rounds ?? [])].sort((a, b)=>(a.createdAt ?? 0) - (b.createdAt ?? 0));
	const newest = ordered[ordered.length - 1];
	if (newest != null && newest.id != round.id) return {ok: false, reason: "staleRound"};

	// a door the CHARACTER has already been handed is still unresolved: do not stack another on top
	const awaitingCharacter = (rounds ?? []).some(a=>a.doorVerdict == "accept" && a.doorDecision == null);
	if (awaitingCharacter) return {ok: false, reason: "alreadyStaged"};

	return {ok: true};
}

/** Wording for a blocked door, so the reveal and the story tab cannot phrase the same refusal differently. */
export function DescribeDoorBlock(reason: DoorBlockReason): string {
	switch (reason) {
		case "ended": return "This story has ended, so its doors no longer lead anywhere.";
		case "noOffer": return "This round did not offer a way out.";
		case "alreadyAnswered": return "This door has already been answered.";
		case "staleRound": return "This is not the story's latest round, so answering its door would move the story backwards. Open the latest round instead.";
		case "alreadyStaged": return "A door is already waiting for the character in the next round. Play that round before accepting another.";
	}
}

/** What is happening with the story's door right now (§11.5 W5).
 *
 * DERIVED from the rounds, never stored: a story-level mirror of "a door is open" is exactly how the old
 * `doorPending` flag went stale. At most one door is ever in play. */
export type DoorState = {
	/** the round that owns the door */
	roundId: string;
	offer: EchoDoorOffer;
	/** the player's authorial verdict; null = still waiting for one */
	verdict: DoorVerdict|null;
	/** the character's in-fiction decision; null = not made yet */
	decision: string|null;
	/** true when the door is staged and round generation must ask the CHARACTER about it */
	awaitingCharacter: boolean;
};

/** The story's live door, or null when there is none.
 *
 * "In play" covers both halves of the design: an offer awaiting the PLAYER's verdict, and an accepted door
 * awaiting the CHARACTER's decision. A door that has been decided is history and returns null. Searches
 * newest-first, since only a recent round's door can still be live. */
export function GetDoorState(rounds: DoorGateRound[]|n): DoorState|null {
	const ordered = [...(rounds ?? [])].sort((a, b)=>(a.createdAt ?? 0) - (b.createdAt ?? 0));
	for (let i = ordered.length - 1; i >= 0; i--) {
		const round = ordered[i];
		const offer = round.doorOffer ?? null;
		if (offer == null) continue;
		if (round.doorDecision != null) return null; // played out
		return {
			roundId: round.id,
			offer,
			verdict: round.doorVerdict ?? null,
			decision: round.doorDecision ?? null,
			awaitingCharacter: round.doorVerdict == "accept" && round.doorDecision == null,
		};
	}
	return null;
}

/** The world a door leads to: its recorded target when that still exists and is still a different place,
 * else a fresh pick (§11.5 W3).
 *
 * The target is normally fixed at WEAVE time, so this is a lookup. The fresh pick only happens when the
 * pool changed underneath the offer (the target world was deleted, or the story is somehow already in it),
 * and that case is reported so the round log can say the description and the destination disagree. */
export type DoorDestination = {
	worldId: string|null;
	worldName: string|null;
	/** true when the offer's recorded target was unusable and a fresh world was picked instead */
	fallback: boolean;
};

export function ResolveDoorDestination(options: {
	offer: EchoDoorOffer|n;
	currentWorldId: string|null|n;
	pool: EchoWorld[]|n;
	rng: () => number;
}): DoorDestination {
	const {offer, currentWorldId, pool, rng} = options;
	const worlds = (pool ?? []).filter(a=>a != null);
	const targetId = offer?.targetWorldId ?? null;

	if (targetId != null && targetId != currentWorldId) {
		const target = worlds.find(a=>a.id == targetId) ?? null;
		if (target != null) return {worldId: target.id, worldName: target.name, fallback: false};
	}

	const next = PickRandomWorld(worlds, rng, currentWorldId ?? null);
	if (next == null) return {worldId: currentWorldId ?? null, worldName: null, fallback: true};
	return {
		worldId: next.id,
		worldName: next.name,
		// a one-entry pool legitimately hands back the world we are already in -- reported, not hidden
		fallback: next.id == currentWorldId,
	};
}

/** Picks the world a NEW door should lead to, at WEAVE time (§11.5 W3). Excludes the current world, and
 * returns null when the pool has nowhere else to go -- an offer that leads nowhere is better left absent
 * than written and then silently redirected. */
export function PickDoorTarget(options: {
	currentWorldId: string|null|n;
	pool: EchoWorld[]|n;
	rng: () => number;
}): EchoWorld|null {
	const {currentWorldId, pool, rng} = options;
	const worlds = (pool ?? []).filter(a=>a != null && a.id != currentWorldId);
	if (worlds.length == 0) return null;
	return PickRandomWorld(worlds, rng, currentWorldId ?? null);
}

/** The visit history after arriving in `worldId` (§14). Appends a NEW visit only when the world actually
 * changed, so a no-op move does not litter the history -- and re-entering a world you left DOES get its own
 * entry, because the second stay is a different stretch of the story.
 *
 * §11.5 W4: the append also records where the character came FROM, why, and (for a door) what the door
 * looked like. Keeping that on the visit rather than deriving it from the neighbours means a later deletion
 * of an intermediate world cannot corrupt the record, and it is what the arrival block quotes. */
export function AppendWorldVisit(
	history: WorldVisit[]|n,
	world: {id: string, name?: string|n}|n,
	roundIndex: number,
	options?: {cause?: WorldChangeCause|n, doorDescription?: string|n},
): WorldVisit[] {
	const list = [...(history ?? [])];
	if (world == null) return list;

	const last = list[list.length - 1];
	if (last != null && last.worldId == world.id) return list;

	list.push({
		worldId: world.id,
		worldName: world.name ?? null,
		startedAtRoundIndex: Math.max(0, Math.round(roundIndex)),
		fromWorldId: last?.worldId ?? null,
		fromWorldName: last?.worldName ?? null,
		cause: options?.cause ?? null,
		doorDescription: options?.doorDescription ?? null,
	});
	return list;
}

/** How many rounds the story has had, which is the index a new world visit is stamped with. Missing on a
 * pre-P11 row, so it is treated as "unknown, count what we can": the round count of the story's rounds is
 * the caller's business, and this only reports the stored value. */
export function StoryRoundIndex(story: {roundCount?: number|n}|n): number {
	const value = story?.roundCount;
	if (value == null || !Number.isFinite(value)) return 0;
	return Math.max(0, Math.round(value));
}

// story state (§8.3)
// ==========

/** Why a story ended. `lucid` = the suspicion meter boiled over (§8.4); `manual` = the user ended it. */
export const StoryEndReason_values = ["lucid", "manual"] as const;
export type StoryEndReason = typeof StoryEndReason_values[number];

export function IsStoryEnded(story: {ended?: boolean|n}|n): boolean {
	return story?.ended == true;
}

/** True when a story was ended BY HAND rather than by the character waking up: a LUCID ending always writes
 * an interlude, a manual one never does. A whitespace-only interlude counts as absent. */
export function IsStoryEndedManually(story: {ended?: boolean|n, lucidInterlude?: string|n}|n): boolean {
	if (story?.ended != true) return false;
	return (story.lucidInterlude ?? "").trim().length == 0;
}

/** Whether the story can be played, with the reason when it cannot (`ended` vs an unresolvable character).
 * Takes the resolved character in, since resolving it is a pool lookup and this module stays pure. */
export type StoryPlayability =
	| {playable: true}
	| {playable: false, reason: "ended"|"characterMissing"};

export function GetStoryPlayability(story: {ended?: boolean|n}|n, resolvedCharacter: unknown|n): StoryPlayability {
	if (story == null) return {playable: false, reason: "ended"};
	if (IsStoryEnded(story)) return {playable: false, reason: "ended"};
	if (resolvedCharacter == null) return {playable: false, reason: "characterMissing"};
	return {playable: true};
}

/** True when the weaver's summary should be trusted as the running summary (§8.1). An empty or
 * whitespace-only `summaryOut` would restart the story's continuity, so it is treated as absent. */
export function HasUsableSummary(summary: string|n): boolean {
	return summary != null && summary.trim().length > 0;
}

// selection (P11 follow-up: many stories may run; one is selected)
// ==========

/** The minimal story shape the selection rule needs. Structural, so the DB class satisfies it. */
export type SelectableStory = {
	id: string;
	createdAt: number;
	ended?: boolean|n;
};

/** Which running story is ACTIVE for the profile's stored selection. Falls back to the newest running one
 * when nothing is selected or the pointer dangles; never returns an ENDED story. */
export function SelectActiveStory<T extends SelectableStory>(stories: T[]|n, activeStoryId: string|n): T|n {
	const running = (stories ?? []).filter(a=>a.ended != true);
	if (activeStoryId != null) {
		const selected = running.find(a=>a.id == activeStoryId) ?? null;
		if (selected != null) return selected;
	}
	return [...running].sort((a, b)=>b.createdAt - a.createdAt)[0] ?? null;
}

/** True when a stored selection points at nothing selectable, so the UI can say the selection was repaired
 * instead of silently showing a story the user did not choose. */
export function IsActiveStorySelectionStale<T extends SelectableStory>(stories: T[]|n, activeStoryId: string|n): boolean {
	if (activeStoryId == null) return false;
	return !(stories ?? []).some(a=>a.id == activeStoryId && a.ended != true);
}

/** The character a NEW story must avoid re-using (§8.3 "Starts a new story with a DIFFERENT character
 * drawn from the pool"). Returns the previous story's character id, or null when there is nothing to
 * avoid. A one-character pool falls back to repeating them -- refusing to start a story would be worse. */
export function PreviousCharacterIdToExclude(previousStory: {characterId?: string|n}|n): string|null {
	return previousStory?.characterId ?? null;
}
